import { ListChecksIcon, TimerIcon, TrophyIcon, Volume2Icon, VolumeXIcon } from "lucide-react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTargetScoreQuery, useUpdateTargetScoreMutation } from "@/hooks/queries";
import { trackEvent } from "@/lib/analytics";
import {
  COUNTDOWN_OPTIONS,
  EXAM_QUESTION_COUNT_OPTIONS,
  type ExamQuestionCountOption,
  TARGET_SCORE_OPTIONS,
  type TargetScoreOption,
} from "@/lib/constant";
import { useSettingsStore } from "@/store/settingsStore";

export function StudySettingsSection() {
  const isMuted = useSettingsStore(state => state.isMuted);
  const setIsMuted = useSettingsStore(state => state.setIsMuted);
  const countdownSeconds = useSettingsStore(state => state.countdownSeconds);
  const setCountdownSeconds = useSettingsStore(state => state.setCountdownSeconds);
  const examQuestionCount = useSettingsStore(state => state.examQuestionCount);
  const setExamQuestionCount = useSettingsStore(state => state.setExamQuestionCount);

  const { data: targetScore = TARGET_SCORE_OPTIONS[2] } = useTargetScoreQuery();
  const { mutate: updateTargetScore } = useUpdateTargetScoreMutation();

  return (
    <section>
      <div className="rounded-xl bg-card overflow-hidden">
        {/* 자동 읽기 */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            {isMuted ? (
              <VolumeXIcon className="size-4 text-muted-foreground" />
            ) : (
              <Volume2Icon className="size-4 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm font-medium text-foreground">자동 읽기</p>
              <p className="text-xs text-muted-foreground">단어를 넘길 때마다 읽어 주기</p>
            </div>
          </div>
          <Switch
            checked={!isMuted}
            onCheckedChange={checked => {
              trackEvent("study_settings_changed", { setting: "auto_read", enabled: checked });
              setIsMuted(!checked);
            }}
          />
        </div>

        {/* 문항 수 */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <ListChecksIcon className="size-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">문항 수</p>
              <p className="text-xs text-muted-foreground">새 테스트의 문제 개수</p>
            </div>
          </div>
          <Select
            value={String(examQuestionCount)}
            onValueChange={value => {
              trackEvent("study_settings_changed", { setting: "exam_question_count", value: Number(value) });
              setExamQuestionCount(Number(value) as ExamQuestionCountOption);
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXAM_QUESTION_COUNT_OPTIONS.map(count => (
                <SelectItem key={count} value={String(count)}>
                  {count}개
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 제한 시간 */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <TimerIcon className="size-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">제한 시간</p>
              <p className="text-xs text-muted-foreground">문제당 답변 제한 시간</p>
            </div>
          </div>
          <Select
            value={String(countdownSeconds)}
            onValueChange={value => {
              trackEvent("study_settings_changed", { setting: "countdown_seconds", value: Number(value) });
              setCountdownSeconds(Number(value));
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTDOWN_OPTIONS.map(sec => (
                <SelectItem key={sec} value={String(sec)}>
                  {sec}초
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 합격 기준 */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <TrophyIcon className="size-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">합격 기준</p>
              <p className="text-xs text-muted-foreground">이 점수 이상이면 합격</p>
            </div>
          </div>
          <Select
            value={String(targetScore)}
            onValueChange={value => {
              trackEvent("study_settings_changed", { setting: "target_score", value: Number(value) });
              updateTargetScore(Number(value) as TargetScoreOption);
            }}
          >
            <SelectTrigger size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TARGET_SCORE_OPTIONS.map(score => (
                <SelectItem key={score} value={String(score)}>
                  {score}점
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </section>
  );
}
